<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { useRoute } from 'vue-router';
import { useInboxStore } from '@/stores/inbox.store';
import { useQuestsStore } from '@/stores/quests.store';
import { useChatUnreadStore } from '@/stores/chat.store';
import { useAuthStore } from '@/stores/auth.store';
import { useOverlaysStore } from '@/stores/overlays.store';
import { useInventoryNewStore } from '@/stores/inventoryNew.store';
import { useResearchStore } from '@/stores/research.store';
import { useSessionStore } from '@/stores/session.store';

/**
 * Lot 35D — Navigation bottom mobile. 4 raccourcis principaux + bouton
 * "Menu" qui ouvre un drawer avec le reste (Lobby, News, Leaderboard,
 * Stats, Admin). Visible uniquement sous 768px (cf AppLayout).
 *
 * Le router prend le relai sur les router-link pour l'active state.
 */

const route = useRoute();
const auth = useAuthStore();
const inbox = useInboxStore();
const quests = useQuestsStore();
const chatUnread = useChatUnreadStore();
const overlays = useOverlaysStore();
const inventoryNew = useInventoryNewStore();
const research = useResearchStore();
const session = useSessionStore();
const drawerOpen = ref(false);

// La bottom-nav peut être la SEULE nav (mobile) : on amorce les compteurs
// passifs (objets neufs, file de recherche) ici aussi.
onMounted(() => {
  if (session.inUniverse) { void inventoryNew.refresh(); void research.refreshGlobalCount(); }
});

/**
 * Destinations méta ouvertes en MODALE (overlay) au lieu de naviguer — même
 * comportement que le header desktop (HeaderGlobalNav). Les routes restent des
 * deep-links. Les entrées du drawer ferment le drawer en s'ouvrant.
 */
const OVERLAY_BY_ROUTE: Record<string, () => void> = {
  '/inbox':            () => overlays.openInbox(),
  '/quests':           () => { overlays.openQuests(); quests.acknowledgeAvailable(); },
  '/inventory':        () => { drawerOpen.value = false; overlays.openInventory(); },
  '/research/global':  () => { drawerOpen.value = false; overlays.openResearchGlobal(); research.acknowledgeResearch(); },
  '/chat':             () => { drawerOpen.value = false; overlays.openChat(); },
};
function hasOverlay(to: string): boolean { return to in OVERLAY_BY_ROUTE; }
function openOverlayFor(to: string): void { OVERLAY_BY_ROUTE[to]?.(); }

// Formatte un compteur badge : 0 -> null (cache), >99 -> "99+", sinon nombre.
function formatBadge(n: number): string | null {
  if (n <= 0) return null;
  return n > 99 ? '99+' : String(n);
}

// Map route -> badge. Route absente du map => pas de badge.
// Chat est dans le drawer "extra" mais on le badge quand meme.
const badges = computed<Record<string, string | null>>(() => ({
  '/inbox':  formatBadge(inbox.unreadCount),
  '/chat':   formatBadge(chatUnread.unreadCount),
  '/quests': formatBadge(quests.claimableCount),
  '/inventory': formatBadge(inventoryNew.newCount),
}));

// Routes au badge passif (cyan, info) plutôt que rouge (alerte).
const PASSIVE_BADGE_ROUTES = new Set(['/inventory']);
// Point passif (sans nombre) : nouvelle quête / recherche dispo, si pas de badge.
function dotFor(to: string): boolean {
  if (to === '/quests') return quests.hasNewQuests;
  if (to === '/research/global') return research.hasNewResearch;
  return false;
}

// 4 slots principaux + bouton Menu : on garde Inbox et Quetes (les deux
// vues qui notifient le joueur en temps reel). Chat / Inventaire /
// Recherches passent dans le drawer "Plus" : utiles mais moins urgent.
const main = [
  { to: '/',       label: 'Planete', icon: 'House' },
  { to: '/cosmos', label: 'Cosmos',  icon: 'Place' },
  { to: '/inbox',  label: 'Inbox',   icon: 'Message' },
  { to: '/quests', label: 'Quetes',  icon: 'Flag' },
] as const;

const extra = [
  { to: '/lobby',          label: 'Lobby univers', icon: 'Compass' },
  { to: '/research/global', label: 'Recherches',   icon: 'Cpu' },
  { to: '/inventory',      label: 'Inventaire',    icon: 'Box' },
  { to: '/chat',           label: 'Chat',          icon: 'ChatDotRound' },
  { to: '/fleets',         label: 'Commandement',  icon: 'Promotion' },
  { to: '/news',           label: 'Actualites',    icon: 'Bell' },
  { to: '/leaderboard',    label: 'Classement',    icon: 'Trophy' },
  { to: '/me/stats',       label: 'Mes stats',     icon: 'TrendCharts' },
  // Communauté & référence — publiques, accessibles partout.
  { to: '/forum',          label: 'Forum',         icon: 'ChatLineSquare' },
  { to: '/library',        label: 'Bibliothèque',  icon: 'Reading' },
  { to: '/admin',          label: 'Admin',         icon: 'Setting' },
] as const;

// Admin réservé aux porteurs d'un rôle admin (le backend 403 sinon).
const visibleExtra = computed(() => extra.filter(e => e.to !== '/admin' || auth.isAdmin));

function isActive(to: string): boolean {
  if (to === '/') return route.path === '/' || route.path.startsWith('/planet');
  return route.path === to || route.path.startsWith(to + '/');
}

const currentExtraActive = computed(() => visibleExtra.value.some(e => isActive(e.to)));
</script>

<template>
  <nav class="mobile-bottom-nav" aria-label="Navigation principale">
    <template v-for="m in main" :key="m.to">
      <!-- Destinations méta (Inbox / Quêtes) : ouvrent une modale au lieu de naviguer. -->
      <button
        v-if="hasOverlay(m.to)"
        type="button"
        class="mbn-item"
        @click="openOverlayFor(m.to)"
      >
        <span class="mbn-icon-wrap">
          <el-icon :size="22"><component :is="m.icon" /></el-icon>
          <span
            v-if="badges[m.to]"
            class="mbn-badge"
            :class="{ 'mbn-badge--passive': PASSIVE_BADGE_ROUTES.has(m.to) }"
            aria-hidden="true"
          >{{ badges[m.to] }}</span>
          <span v-else-if="dotFor(m.to)" class="mbn-dot" aria-hidden="true" />
        </span>
        <span class="mbn-label">{{ m.label }}</span>
      </button>
      <router-link
        v-else
        :to="m.to"
        class="mbn-item"
        :class="{ 'is-active': isActive(m.to) }"
      >
        <span class="mbn-icon-wrap">
          <el-icon :size="22"><component :is="m.icon" /></el-icon>
          <span
            v-if="badges[m.to]"
            class="mbn-badge"
            :class="{ 'mbn-badge--passive': PASSIVE_BADGE_ROUTES.has(m.to) }"
            aria-hidden="true"
          >{{ badges[m.to] }}</span>
          <span v-else-if="dotFor(m.to)" class="mbn-dot" aria-hidden="true" />
        </span>
        <span class="mbn-label">{{ m.label }}</span>
      </router-link>
    </template>

    <button
      type="button"
      class="mbn-item mbn-menu-button"
      :class="{ 'is-active': currentExtraActive }"
      @click="drawerOpen = true"
    >
      <el-icon :size="22"><Menu /></el-icon>
      <span class="mbn-label">Menu</span>
    </button>

    <el-drawer
      v-model="drawerOpen"
      direction="btt"
      size="60%"
      :with-header="true"
      title="Autres"
      append-to-body
      class="mbn-drawer"
    >
      <ul class="mbn-extra-list">
        <li v-for="e in visibleExtra" :key="e.to">
          <button
            v-if="hasOverlay(e.to)"
            type="button"
            class="mbn-extra-item mbn-extra-button"
            @click="openOverlayFor(e.to)"
          >
            <el-icon :size="20"><component :is="e.icon" /></el-icon>
            <span>{{ e.label }}</span>
            <span
              v-if="badges[e.to]"
              class="mbn-extra-badge"
              :class="{ 'mbn-extra-badge--passive': PASSIVE_BADGE_ROUTES.has(e.to) }"
            >{{ badges[e.to] }}</span>
            <span v-else-if="dotFor(e.to)" class="mbn-extra-dot" aria-hidden="true" />
          </button>
          <router-link
            v-else
            :to="e.to"
            class="mbn-extra-item"
            :class="{ 'is-active': isActive(e.to) }"
            @click="drawerOpen = false"
          >
            <el-icon :size="20"><component :is="e.icon" /></el-icon>
            <span>{{ e.label }}</span>
            <span
              v-if="badges[e.to]"
              class="mbn-extra-badge"
              :class="{ 'mbn-extra-badge--passive': PASSIVE_BADGE_ROUTES.has(e.to) }"
            >{{ badges[e.to] }}</span>
            <span v-else-if="dotFor(e.to)" class="mbn-extra-dot" aria-hidden="true" />
          </router-link>
        </li>
      </ul>
    </el-drawer>
  </nav>
</template>

<style lang="scss" scoped>
.mobile-bottom-nav {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 100;
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  background: rgba(13, 18, 36, 0.96);
  backdrop-filter: blur(8px);
  border-top: 1px solid $border-default;
  padding: 4px 0 max(4px, env(safe-area-inset-bottom));
}

.mbn-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2px;
  padding: 6px 4px;
  color: $text-muted;
  text-decoration: none;
  font-size: 10px;
  letter-spacing: 0.05em;
  border: none;
  background: transparent;
  cursor: pointer;
  transition: color $duration-fast $easing-default;

  &:hover,
  &.is-active {
    color: $color-neon-cyan;
  }
}

.mbn-icon-wrap {
  position: relative;
  display: inline-flex;
}

.mbn-badge {
  position: absolute;
  top: -4px;
  right: -8px;
  min-width: 16px;
  height: 16px;
  padding: 0 4px;
  border-radius: 8px;
  background: $color-danger;
  color: #fff;
  font-size: 10px;
  font-weight: 700;
  line-height: 16px;
  text-align: center;
  font-family: $font-mono;
  box-shadow: 0 0 0 2px $bg-primary;
  pointer-events: none;

  &--passive { background: $color-neon-cyan; color: #04222b; }
}
// Point passif (nouveauté sans compteur).
.mbn-dot {
  position: absolute;
  top: -2px;
  right: -4px;
  width: 9px;
  height: 9px;
  border-radius: 50%;
  background: $color-neon-cyan;
  box-shadow: 0 0 6px rgba($color-neon-cyan, 0.9), 0 0 0 2px $bg-primary;
  pointer-events: none;
}

.mbn-label {
  font-family: $font-mono;
  text-transform: uppercase;
}

.mbn-menu-button {
  font: inherit;
}

.mbn-extra-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
}

.mbn-extra-item {
  display: flex;
  align-items: center;
  gap: $space-3;
  padding: $space-3 $space-4;
  color: $text-primary;
  text-decoration: none;
  border-bottom: 1px solid $border-subtle;
  font-size: $font-size-md;

  &:hover {
    background: $bg-tertiary;
    color: $color-neon-cyan;
  }

  &.is-active {
    color: $color-neon-cyan;
    background: rgba($accent-primary, 0.12);
  }
}

.mbn-extra-button {
  width: 100%;
  border: none;
  background: transparent;
  cursor: pointer;
  font: inherit;
  text-align: left;
}

.mbn-extra-badge {
  margin-left: auto;
  min-width: 22px;
  padding: 0 6px;
  border-radius: 11px;
  background: $color-danger;
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  line-height: 18px;
  text-align: center;
  font-family: $font-mono;

  &--passive { background: $color-neon-cyan; color: #04222b; }
}
// Point passif dans le drawer (aligné à droite comme le badge).
.mbn-extra-dot {
  margin-left: auto;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: $color-neon-cyan;
  box-shadow: 0 0 6px rgba($color-neon-cyan, 0.9);
}
</style>
